import React from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, ShieldAlert, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function VerifyDriverPage() {
  const { token } = useParams();
  const [state, setState] = React.useState({ loading: true, data: null, error: "" });

  React.useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return;
    axios
      .get(`${base}/api/verify-driver/${encodeURIComponent(token)}`)
      .then((r) => setState({ loading: false, data: r.data, error: "" }))
      .catch((e) => setState({ loading: false, data: null, error: e.response?.data?.detail || "Could not verify" }));
  }, [token]);

  const { loading, data, error } = state;
  const ok = data && data.found && data.verified;
  const pendingChecks = data && data.found && !data.verified;
  const notFound = data && !data.found;

  return (
    <div data-testid="verify-driver-page" className="min-h-screen bg-[#F5F5F5] py-12 px-4">
      <div className="max-w-md mx-auto">
        <Link to="/" data-testid="verify-driver-back" className="inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#0A0A0A] mb-6">
          <ArrowLeft size={14} /> Back to raanah.com
        </Link>

        <div className="bg-white border border-[#0A0A0A]/10 overflow-hidden">
          {/* Brand bar */}
          <div className="bg-[#1E4D55] text-white px-6 py-5">
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-80">Raanah · Driver verification</p>
            <p className="font-display text-2xl font-black tracking-tighter mt-1">Trusted at the door</p>
          </div>

          {loading && (
            <div className="p-10 text-center" data-testid="verify-driver-loading">
              <Loader2 size={28} className="animate-spin text-[#1E4D55] mx-auto" />
              <p className="text-sm text-[#475569] mt-3">Checking driver <span className="font-mono">{token}</span>&hellip;</p>
            </div>
          )}

          {!loading && (notFound || error) && (
            <div className="p-8" data-testid="verify-driver-not-found">
              <div className="flex items-start gap-3 bg-[#FEE2E2] border border-[#FCA5A5] p-4">
                <ShieldAlert size={20} className="text-[#B91C1C] shrink-0 mt-0.5" />
                <div>
                  <p className="font-display font-bold text-[#B91C1C]">No Raanah driver matches this code.</p>
                  <p className="text-sm text-[#7F1D1D] mt-1">
                    Don&apos;t let them in. If you have a Raanah booking, call us using the number below to confirm who&apos;s on their way.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!loading && ok && (
            <div className="p-6" data-testid="verify-driver-success">
              <div className="flex items-start gap-3 bg-[#5BB247]/10 border border-[#5BB247]/40 p-4 mb-5">
                <ShieldCheck size={20} className="text-[#2c8642] shrink-0 mt-0.5" />
                <div>
                  <p className="font-display font-bold text-[#2c8642]">Verified by Raanah.</p>
                  <p className="text-sm text-[#1f5d2e] mt-1">
                    {data.display_name} is a fully-checked Raanah driver
                    {data.joined_year ? ` (active since ${data.joined_year})` : ""}.
                  </p>
                </div>
              </div>

              <div className="mb-5 flex items-center gap-4">
                {data.profile_photo_url && (
                  <img
                    src={data.profile_photo_url}
                    alt={data.display_name}
                    data-testid="verify-driver-photo"
                    className="w-16 h-16 object-cover border border-[#0A0A0A]/15 shrink-0"
                  />
                )}
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#475569]">Driver</p>
                  <p className="font-display text-2xl font-black text-[#0A0A0A]">{data.display_name}</p>
                  <p className="text-xs text-[#475569] mt-1">Last name shortened for privacy. Full name on the booking you received.</p>
                </div>
              </div>

              {data.driver_type === "managed" ? (
                <div data-testid="verify-driver-managed-note" className="bg-[#1E4D55]/5 border border-[#1E4D55]/30 p-4">
                  <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#1E4D55] mb-1">
                    Raanah team member
                  </p>
                  <p className="text-sm text-[#0A0A0A]">
                    {data.display_name} is an internal Raanah driver operating our own fleet.
                    Insurance, vehicle checks and driver conduct are managed by Raanah directly.
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#475569] mb-2">
                    Approved documents ({data.required_approved}/{data.required_total} core)
                  </p>
                  <ul className="space-y-1.5" data-testid="verify-driver-doc-list">
                    {data.approved_docs.map((d) => (
                      <li key={d.doc_type} data-testid={`verify-doc-${d.doc_type}`} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 size={16} className="text-[#5BB247] shrink-0 mt-0.5" />
                        <span className="text-[#0A0A0A]">
                          {d.label}
                          {d.valid_until && <span className="text-xs text-[#475569] ml-2">valid until {d.valid_until}</span>}
                        </span>
                      </li>
                    ))}
                    {data.approved_docs.length === 0 && (
                      <li className="text-xs text-[#475569] italic">No documents approved yet.</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}

          {!loading && pendingChecks && (
            <div className="p-8" data-testid="verify-driver-pending">
              <div className="flex items-start gap-3 bg-[#F5A623]/10 border border-[#F5A623]/40 p-4">
                <ShieldAlert size={20} className="text-[#A66800] shrink-0 mt-0.5" />
                <div>
                  <p className="font-display font-bold text-[#A66800]">Verification still in progress.</p>
                  <p className="text-sm text-[#7A4F0F] mt-1">
                    {data.display_name} is registered with Raanah but hasn&apos;t completed all required checks ({data.required_approved}/{data.required_total} done). Call us before accepting their service.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!loading && data && (
            <div className="bg-[#0A0A0A] text-white px-6 py-5">
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/70">Questions? Call Raanah</p>
              <p className="mt-2 font-mono"><strong>{data.hotline}</strong> · <a className="underline" href={`mailto:${data.contact_email}`}>{data.contact_email}</a></p>
              {data.return_address && <p className="mt-1 text-xs text-white/70">{data.return_address}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
